import { isFunction } from '@sonar-software/sonar-ts-lib';
import { QUERY } from '@sonar-software/sonar-ts-constants';
import { useEffect } from 'react';

import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import {
	useCurrentOrganization,
	useOnboardingProgress,
	useSalesforceOrganizations,
	useSwitchOrganization
} from '@hooks';

type RemoveQueryParamOptions = {
	navigate: NavigateFunction;
	onRemove?: (value: string | null) => void;
	param: string;
	route: string | ((value: string | null) => void);
	searchParams: URLSearchParams;
};

function removeQueryParam(options: RemoveQueryParamOptions): void {
	const { navigate, onRemove, param, route, searchParams } = options;

	if (searchParams.has(param)) {
		const paramValue = searchParams.get(param);
		searchParams.delete(param);

		const baseRoute = isFunction(route) ? route(paramValue) : route;
		const searchString = searchParams.toString();
		navigate(`${baseRoute}${searchString && `?${searchString}`}`);

		if (onRemove) {
			onRemove(paramValue);
		}
	}
}

export const RedirectWatcher: React.FC = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { progress } = useOnboardingProgress();
	const switchOrganization = useSwitchOrganization();
	const selectedOrganization = useCurrentOrganization();
	const salesforceOrganizations = useSalesforceOrganizations();

	const { search } = window.location;

	useEffect(() => {
		const searchParams = new URLSearchParams(search);

		removeQueryParam({
			navigate,
			onRemove: (orgId) => {
				switchOrganization({ organizationId: Number(orgId) });
			},
			param: QUERY.TARGET_ORGANIZATION,
			route: pathname,
			searchParams
		});

		removeQueryParam({
			navigate,
			param: QUERY.TARGET_ROUTE,
			route: (targetRoute) => targetRoute,
			searchParams
		});
	}, [ navigate, pathname, search, switchOrganization ]);

	useEffect(() => {
		const hasValidRootOrganization = progress?.organizationId && progress.isSalesforceSynced;
		const hasValidOrganizations = salesforceOrganizations.length;

		if (!selectedOrganization && (hasValidRootOrganization || hasValidOrganizations)) {
			switchOrganization({ organizationId: salesforceOrganizations[ 0 ]?.id ?? progress?.organizationId });
		}
	}, [
		progress?.isSalesforceSynced,
		progress?.organizationId,
		salesforceOrganizations,
		selectedOrganization,
		switchOrganization
	]);

	return null;
};
